import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { 
  ShoppingCart, 
  User, 
  LogOut, 
  Truck, 
  LayoutDashboard,
  Menu,
  X,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { DispatchTopBar } from './DispatchTopBar';
import { ModernNavbar } from './ModernNavbar';
import { useTheme } from '../context/ThemeContext';

export function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isModern } = useTheme();
  const [refreshing, setRefreshing] = useState(false);

  if (isModern && user?.role === 'customer') return <ModernNavbar />;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Phase 7-lite — universal "REFRESH" trigger. Reloads the page which
  // re-runs every dashboard's fetchers + reconnects SSE in one shot. The
  // brief spin gives visual feedback so the user knows the tap registered.
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => window.location.reload(), 250);
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'customer': return '/customer';
      case 'driver': return '/driver';
      case 'dispatcher': return '/dispatch';
      case 'super_admin': return '/dispatch';
      default: return '/login';
    }
  };

  const getDashboardIcon = () => {
    if (!user) return <User className="w-4 h-4" />;
    switch (user.role) {
      case 'driver': return <Truck className="w-4 h-4" />;
      case 'dispatcher':
      case 'super_admin':
        return <LayoutDashboard className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  return (
    <>
    <nav
      className="bg-[#0C0612] border-b border-[#39FF14] sticky top-0 z-50"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="text-[#39FF14] font-mono text-lg font-bold tracking-tight neon-text">
              {'>'} ASH&MERCH_
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {user && user.role === 'customer' && (
              <Link 
                to="/customer/cart" 
                className="relative p-2 text-[#A3C7A8] hover:text-[#39FF14] transition-colors"
                data-testid="nav-cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#39FF14] text-[#050003] text-xs font-bold w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <>
                {user.role === 'customer' && (
                  <Link
                    to="/contact"
                    className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#A3C7A8] hover:text-[#39FF14] transition-colors border border-[#2A0F3D] hover:border-[#39FF14] text-sm font-mono uppercase"
                    data-testid="nav-contact"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Contact
                  </Link>
                )}
                <Link 
                  to={getDashboardLink()}
                  className="flex items-center gap-2 px-3 py-2 text-[#A3C7A8] hover:text-[#39FF14] transition-colors border border-[#2A0F3D] hover:border-[#39FF14]"
                  data-testid="nav-dashboard"
                >
                  {getDashboardIcon()}
                  <span className="text-sm font-mono uppercase">{user.role}</span>
                </Link>
                <button
                  onClick={handleRefresh}
                  className="p-2 text-[#A3C7A8] hover:text-[#00F0FF] transition-colors border border-[#2A0F3D] hover:border-[#00F0FF]"
                  data-testid="nav-refresh"
                  aria-label="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] border border-transparent hover:border-[#FF3B30]"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  LOGOUT
                </Button>
              </>
            ) : (
              <Link 
                to="/login"
                className="px-4 py-2 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] transition-all uppercase tracking-wider text-sm"
                data-testid="nav-login"
              >
                LOGIN
              </Link>
            )}
          </div>

          {/* Mobile right-side: refresh + cart (customers) + hamburger */}
          <div className="md:hidden flex items-center gap-1">
            {user && (
              <button
                onClick={handleRefresh}
                className="p-2 text-[#A3C7A8] hover:text-[#00F0FF] transition-colors"
                data-testid="nav-refresh-mobile"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            {user && user.role === 'customer' && (
              <Link
                to="/customer/cart"
                className="relative p-2 text-[#A3C7A8] hover:text-[#39FF14] transition-colors"
                data-testid="nav-cart-mobile"
                aria-label="Cart"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#39FF14] text-[#050003] text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}
            <button
              className="p-2 text-[#39FF14]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#2A0F3D] py-4 space-y-2">
            {user ? (
              <>
                <Link 
                  to={getDashboardLink()}
                  className="flex items-center gap-2 px-4 py-2 text-[#A3C7A8] hover:text-[#39FF14]"
                  onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); }}
                  data-testid="nav-mobile-dashboard"
                >
                  {getDashboardIcon()}
                  Dashboard
                </Link>
                {user.role === 'customer' && (
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 px-4 py-2 text-[#A3C7A8] hover:text-[#39FF14]"
                    onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); }}
                    data-testid="nav-mobile-contact"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Contact dispatch
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-2 text-[#FF3B30] w-full text-left"
                  data-testid="nav-mobile-logout"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </>
            ) : (
              <Link 
                to="/login"
                className="block px-4 py-2 text-[#39FF14]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
    {/* Feb 2026 (Step 3 · Slice 2 · step 2) — Breadcrumb strip below the
        main navbar; self-hides for non-dispatch scopes and unauthorized users. */}
    <DispatchTopBar />
    </>
  );
}
